// Image service for finding/generating images for fairy tales

// For the MVP, we'll use Unsplash API to find safe, royalty-free images
// Later this could be replaced with a generative AI service
const UNSPLASH_API_URL = 'https://api.unsplash.com/search/photos';
const UNSPLASH_ACCESS_KEY = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;

// Fallback image if we can't find or generate one
const FALLBACK_IMAGE_URL = 'https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?q=80&w=1000&auto=format&fit=crop';

/**
 * Fetches an image based on keywords from a story scene
 * @param {string} keywords - Keywords describing the scene
 * @param {string} ageGroup - The age group ID to ensure appropriate content
 * @returns {Promise<string>} - URL of the selected image
 */
export async function getImageForScene(keywords, ageGroup) {
  try {
    // If no API key, use fallback immediately
    if (!UNSPLASH_ACCESS_KEY) {
      console.warn('[image-service] No Unsplash API key provided, using fallback image');
      return FALLBACK_IMAGE_URL;
    }
    
    // Add fairy tale and age-appropriate keywords
    const searchQuery = `${keywords} fairy tale children illustration`;
    
    // Fetch images from Unsplash
    const response = await fetch(
      `${UNSPLASH_API_URL}?query=${encodeURIComponent(searchQuery)}&per_page=5`,
      {
        headers: {
          'Authorization': `Client-ID ${UNSPLASH_ACCESS_KEY}`
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Unsplash API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // If no results, use fallback
    if (!data.results || data.results.length === 0) {
      return FALLBACK_IMAGE_URL;
    }
    
    // Get random image from results to add variety
    const randomIndex = Math.floor(Math.random() * Math.min(data.results.length, 5));
    return data.results[randomIndex].urls.regular;
    
  } catch (error) {
    console.error('[image-service] Error fetching image:', error);
    // Return fallback image on error
    return FALLBACK_IMAGE_URL;
  }
}

/**
 * Fetches a set of alternative images for the same keywords
 * @param {string} keywords - Keywords describing the scene
 * @param {string} currentImageUrl - Current image URL to exclude
 * @returns {Promise<Array<string>>} - Array of alternative image URLs
 */
export async function getAlternativeImages(keywords, currentImageUrl) {
  try {
    if (!UNSPLASH_ACCESS_KEY) {
      return [FALLBACK_IMAGE_URL];
    }
    
    // Add fairy tale and age-appropriate keywords
    const searchQuery = `${keywords} fairy tale children illustration`;
    
    // Fetch more images from Unsplash
    const response = await fetch(
      `${UNSPLASH_API_URL}?query=${encodeURIComponent(searchQuery)}&per_page=10`,
      {
        headers: {
          'Authorization': `Client-ID ${UNSPLASH_ACCESS_KEY}`
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Unsplash API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // If no results, use fallback
    if (!data.results || data.results.length === 0) {
      return [FALLBACK_IMAGE_URL];
    }
    
    // Filter out the current image and return up to 3 alternatives
    return data.results
      .filter(img => img.urls.regular !== currentImageUrl)
      .slice(0, 3)
      .map(img => img.urls.regular);
      
  } catch (error) {
    console.error('[image-service] Error fetching alternative images:', error);
    return [FALLBACK_IMAGE_URL];
  }
}

/**
 * Process all scenes in a story to add image URLs
 * @param {Object} story - The story object with scenes that have imageKeywords
 * @returns {Promise<Object>} - The story with image URLs added to scenes
 */
export async function addImagesToStory(story) {
  if (!story || !story.scenes || !Array.isArray(story.scenes)) {
    throw new Error('Invalid story format');
  }
  
  try {
    // Process each scene to get an image
    const scenesWithImages = await Promise.all(
      story.scenes.map(async (scene) => {
        const imageUrl = await getImageForScene(scene.imageKeywords, story.ageGroup);
        return {
          ...scene,
          imageUrl
        };
      })
    );
    
    // Update story with images
    return {
      ...story,
      scenes: scenesWithImages
    };
    
  } catch (error) {
    console.error('[image-service] Error adding images to story:', error);
    throw error;
  }
}
